import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { skipToken } from "@reduxjs/toolkit/query";

import BaseAvatar from "@/components_v2/Avatar/BaseAvatar";
import BaseTag from "@/components_v2/Tags/BaseTag";
import { useGetUserByIdQuery } from "@/redux/service";
import {
  certificationPaths,
  certificationRouteKey,
} from "@/domains/certifications/routes";
import {
  formatCertificationDate,
  getInitials,
  getWeekNumber,
} from "@/domains/certifications/utils";
import ContributionBody from "@/features/certifications/community-flags/components/ContributionBody";
import { FROM_DISCUSSION_FLAGS } from "@/features/certifications/community-flags/constants";
import { useContributionContext } from "@/features/certifications/community-flags/hooks/useContributionContext";
import { NotCountedCardProps } from "@/features/certifications/community-flags/types";

import "../styles.css";

const NotCountedCard = ({ certification, grade }: NotCountedCardProps) => {
  const { t } = useTranslation();
  const context = useContributionContext(grade);
  const decidedBy = useGetUserByIdQuery(grade.decidedBy ?? skipToken);

  const name = context.author?.fullName.trim() || grade.authorEmail;
  const week = getWeekNumber(certification.modules, grade.module);
  const adminName =
    decidedBy.data?.fullName.trim() ||
    decidedBy.data?.email ||
    t("certification.admin.communityFlags.notCounted.anAdmin");

  return (
    <article className="flag-card flex flex-col">
      <header className="flag-card-header flex flex-wrap items-center justify-between gap-200">
        <div className="flex items-center gap-200">
          <BaseAvatar
            size="default"
            className="flag-card-avatar"
            label={getInitials(name)}
            imageUrl={context.author?.profilePicture}
            aria-hidden
          />

          <Link
            to={certificationPaths.participant(
              certificationRouteKey(certification),
              grade.author,
            )}
            state={FROM_DISCUSSION_FLAGS}
            className="flag-card-author body-s-medium"
          >
            {name}
          </Link>
        </div>

        <div className="flex flex-wrap items-center gap-200">
          <BaseTag
            size="extra-small"
            label={t(
              `certification.admin.communityFlags.kind.${grade.contentKind}`,
            )}
          />

          {week !== null && (
            <BaseTag
              size="extra-small"
              label={t("certification.labels.week", { position: week })}
            />
          )}

          <span className="flag-card-muted body-xs-regular">
            {t("certification.admin.communityFlags.notCounted.posted", {
              date: formatCertificationDate(grade.submittedAt),
            })}
          </span>
        </div>
      </header>

      <ContributionBody
        certification={certification}
        grade={grade}
        context={context}
      />

      <footer className="flag-card-footer flex flex-wrap items-end justify-between gap-300">
        <div className="flex flex-col gap-50">
          <span className="flag-card-muted body-xs-regular">
            {grade.decidedAt
              ? t("certification.admin.communityFlags.notCounted.markedBy", {
                  name: adminName,
                  date: formatCertificationDate(grade.decidedAt),
                })
              : t("certification.admin.communityFlags.notCounted.marked", {
                  name: adminName,
                })}
          </span>

          {grade.feedback && (
            <p className="flag-card-note body-s-regular">
              {t("certification.admin.communityFlags.notCounted.feedback", {
                feedback: grade.feedback,
              })}
            </p>
          )}
        </div>

        <span className="flag-card-muted body-xs-regular">
          {t("certification.admin.communityFlags.notCounted.waiting")}
        </span>
      </footer>
    </article>
  );
};

export default NotCountedCard;
